import ActionButton from 'components/ActionButton';
import AddInventoryItemModal from 'components/AddInventoryItemModal';
import Currency from 'components/Currency/Currency';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { fetchRelatedItemsForItem, removeRelatedItemFromItem } from 'services';
import { USER_ROLES, isUserAuthorized, showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import { addRelatedItemsToItem } from 'services/api/inventoryItems';

const RelatedItems = props => {
  const [loading, setLoading] = useState<boolean>(true);
  const [items, setItems] = useState<Record<string, any>[]>([]);
  const { modalOpen, toggleModal, userRoles } = props;
  const isUserAuthorizedToEdit = isUserAuthorized(userRoles, [USER_ROLES.PACKER, USER_ROLES.SUPERVISOR, USER_ROLES.MANAGER, USER_ROLES.ADMIN]);

  const fetchRelatedItems = async () => {
    try {
      const response = await fetchRelatedItemsForItem(props.itemId);
      setItems(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchRelatedItems();
  }, []);
  
  const removeRelatedItem = async (id: string) => {
    const relatedItemIds = [id];
    try {
      await removeRelatedItemFromItem(props.itemId, relatedItemIds);
      await fetchRelatedItems();
      showSuccess('Related Item removed successfully.');
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(() => [
    {
      name: 'ItemID',
      selector: 'objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/inventory-items/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Item Name',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Description',
      selector: 'description',
      sortable: true,
      width: '400px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <div className={styles['related-item-description']}>
          {row.description}
        </div>
      ),
    },
    {
      name: 'Price',
      selector: 'price',
      sortable: true,
      width: '100px',
      // eslint-disable-next-line react/display-name
      format: row => <Currency amount={parseFloat(row.price?.toFixed(2))} default={'-'} />,
    },
    {
      name: 'Quantity',
      selector: 'stock',
      sortable: true,
      width: '100px',
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      omit: !isUserAuthorizedToEdit,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={() => removeRelatedItem(row.objectId)}
          />
        </div>
      ),
    },
  ], []);

  const addRelatedItem = async (params) => {
    const { selectedItems = [] } = params;
    const selectedItemIDs = selectedItems.map(item => item['id']);
    setLoading(true);
    try {
      await addRelatedItemsToItem(props.itemId, selectedItemIDs);
      showSuccess('Related Item(s) added successfully');
      await fetchRelatedItems();
      toggleModal();
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Loading type={'mini'} />}
      {modalOpen && (
        <AddInventoryItemModal
          itemId={props.itemId}
          relatedItems={items}
          toggleModal={toggleModal}
          onSubmit={addRelatedItem}
          heading="Add A New Related Item"
          forRelatedItems
        />
      )}
      <div className={styles['related-items-container']}>
        <div className={styles['section-header']}>
          <div className={styles['section-heading']}>Related Items</div>
          {isUserAuthorizedToEdit && <ActionButton
            active={true}
            isLoading={false}
            onClick={toggleModal}
            text={<>Related Item <span className={'ml-3'}>+</span></>}
          />}
        </div>
        <DataTable
          keyField= "objectid"
          columns={columns}
          noHeader={true}
          data={items}
          highlightOnHover={true}
          defaultSortField="reatedAt"
          customStyles={tableStyles}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default RelatedItems;